import {
  LabelSelector,
  type SeverityLabelOption,
  type ScoreLabelOption,
  type SeverityConfig,
  type ScoreConfig,
  getSeverityLabel,
  getScoreLabel,
  getScoreDescription,
} from '@core/components/LabelSelector';
import { Badge, BadgeVariant, Card } from '@soteria/design-system-ve';
import { ContentType } from '@system-config/label-management/domain';
import { useStandardLabels } from '@system-config/label-management/hooks';
import { useMemo } from 'react';

import type { DialogPolicyInfo } from '../../types';
import { useDialogContext } from '../DialogProvider';

import {
  policyInfoSummaryCard,
  cardHeader,
  cardTitle,
  cardContent,
  infoField,
  fieldLabel,
  fieldValue,
  emptyState,
  policyStatusContainer,
  policyStatusText,
} from './ReviewStep.css';
import { stepFormStyles } from './Steps.css';

/**
 * Configuration for severity-based labels
 */
const SEVERITY_CONFIG: SeverityConfig = {
  mode: 'severity' as const,
  min: 1,
  defaultValue: 2,
  defaultMax: 3,
  showDescription: true,
  getValueLabel: getSeverityLabel,
};

/**
 * Configuration for score threshold-based labels
 */
const SCORE_CONFIG: ScoreConfig = {
  mode: 'score' as const,
  min: 0,
  max: 100,
  defaultValue: 70,
  showDescription: true,
  getValueLabel: getScoreLabel,
  getValueDescription: getScoreDescription,
};

/**
 * PolicyInfo component - displays policy basic information in a clean card format
 */
function PolicyInfoSummary({ policyInfo }: { policyInfo: DialogPolicyInfo }) {
  return (
    <Card aria-labelledby="policy-info-title" className={policyInfoSummaryCard} elevation="low" padding="medium">
      <div className={cardHeader}>
        <h4 className={cardTitle} id="policy-info-title">
          Policy Information
        </h4>
      </div>
      <div className={cardContent}>
        <div className={infoField}>
          <span className={fieldLabel}>Name:</span>
          <span className={fieldValue}>{policyInfo.name || <em className={emptyState}>Not specified</em>}</span>
        </div>
        <div className={infoField}>
          <span className={fieldLabel}>Description:</span>
          <span className={fieldValue}>{policyInfo.description || <em className={emptyState}>No description provided</em>}</span>
        </div>
        <div className={infoField}>
          <span className={fieldLabel}>Policy Status:</span>
          <div className={policyStatusContainer}>
            <Badge text={policyInfo.isEnabled ? 'Enabled' : 'Disabled'} variant={policyInfo.isEnabled ? BadgeVariant.Success : BadgeVariant.Error} />
            <span className={policyStatusText}>
              {policyInfo.isEnabled ? 'This policy will actively monitor content' : 'This policy will not be applied to content'}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * ReviewStep component - Step 4 of the dialog
 * Shows a comprehensive, well-organized summary of all configured policy data for final review
 */
export function ReviewStep() {
  const { state } = useDialogContext();
  const { formData } = state;
  const { policyInfo, textLabels, imageLabels } = formData;
  const { labels: standardLabels, loading: labelsLoading } = useStandardLabels();

  // Filter standard labels for text content
  const textLabelOptions: SeverityLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Text)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
          maxSeverity: label.maxSeverity ?? undefined,
        })),
    [standardLabels]
  );

  // Filter standard labels for image content
  const imageLabelOptions: ScoreLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Image)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
        })),
    [standardLabels]
  );

  return (
    <div className={stepFormStyles}>
      <PolicyInfoSummary policyInfo={policyInfo} />

      {/* Text Content Labels */}
      {textLabels.textLabels.length > 0 && (
        <LabelSelector
          readOnly
          availableLabels={textLabelOptions}
          config={SEVERITY_CONFIG}
          emptyStateMessage="No text labels selected."
          enableBulkSelection={false}
          loading={labelsLoading}
          selectedLabels={textLabels.textLabels}
          title="Text Content Labels"
          onChange={() => {
            /* Read-only in review step */
          }}
        />
      )}

      {/* Image Content Labels */}
      {imageLabels.imageLabels.length > 0 && (
        <LabelSelector
          readOnly
          availableLabels={imageLabelOptions}
          config={SCORE_CONFIG}
          emptyStateMessage="No image labels selected."
          enableBulkSelection={false}
          loading={labelsLoading}
          selectedLabels={imageLabels.imageLabels}
          title="Image Content Labels"
          onChange={() => {
            /* Read-only in review step */
          }}
        />
      )}
    </div>
  );
}
